// Meta demographics — the PURE read core. No I/O, no clock reads, no BigQuery.
//
// This is the first read path over `ad_performance_demographics`. Everything the
// panel is allowed to claim is decided HERE rather than in a template, for the
// same reason the client-portal series core exists: a rule that lives in JSX is a
// rule one `?? 0` undoes.
//
// ⚠️⚠️ SIX RULES, AND THEY ARE THE POINT OF THIS FILE.
//
//  1. THE GRAIN. Demographics rows are keyed to a PULL WINDOW, not a day —
//     `getAdAnalytics` takes no time increment, so a breakdown cell is only
//     meaningful relative to the window it was computed over. So a range cannot
//     be filtered the way the daily facts can. We use DAILY rows (a window where
//     start === end) whenever ANY fall inside the range, and say which days they
//     actually cover; only with NO daily rows at all do we fall back to the
//     latest window that COVERS the range. A window that merely OVERLAPS is
//     never used, and no covering window means we show nothing WITH A REASON —
//     never the nearest window silently.
//
//     ⚠️⚠️ THE ORIGINAL RULE WAS "ALL DAILY OR NO DAILY", AND IT WAS WRONG.
//     It required a daily row on EVERY day of the range, on the reasoning that
//     a partial series has an ambiguous hole in it. That reasoning is real, but
//     it is already answered by rule 2 — the stated period is the period the
//     FIGURES describe — and layering an all-or-nothing test on top of it threw
//     away good data to avoid a labelling problem that was already solved.
//     Live, 2026-08-31: the nightly demographics job had failed for ONE night
//     (a scheduler created without run.invoker), so 30 August had no daily row.
//     The report's range was 1-30 Aug and the daily rows covered 1-29 — so 29
//     days of EXACT data were discarded, no covering window spanned the month,
//     and the audience section then removed itself with no explanation.
//
//     ⚠️ THERE IS DELIBERATELY NO COVERAGE THRESHOLD. One measured day inside a
//     30-day range is shown as that one day, labelled as that one day: narrower
//     and more honest than a 91-day covering window, and it needs no number to
//     tune. If that judgement is ever overruled, the threshold goes in ONE place
//     — the `dailyDays.size > 0` test below.
//
//  2. THE STATED PERIOD IS THE STORED WINDOW, NEVER THE SELECTED RANGE. On the
//     fallback path every other panel on the page says "1-15 Aug" and this one is
//     showing, say, 1 Jul - 27 Aug. Widening the label to match the report is the
//     single change that turns a correct panel into a misleading one.
//
//  3. NO RECONCILIATION WITH THE HEADLINE. These cells will not sum to the
//     report's totals — Meta withholds unknown buckets, dedups across breakdowns,
//     and on the fallback path the window differs as well. So every share is of
//     THE BREAKDOWN'S OWN TOTAL, and that denominator travels with it
//     (`totals`), so a caller cannot print a share without the number it is a
//     share of.
//
//  4. ⚠️⚠️ THERE IS NO RELIABILITY FLOOR. EVERY SHARE IS SHOWN, at every size.
//     A 1-lead cell in a 100-lead breakdown reads "1 · 1.0%"; a 1-lead cell in a
//     3-lead breakdown reads "1 · 33.3%". Nothing is folded, nothing is dropped,
//     and no percentage is withheld — so the named rows sum to the stated
//     denominator EXACTLY.
//
//     ⚠️⚠️ RULE 3'S STATED DENOMINATOR IS WHAT MAKES THAT HONEST, and it is the
//     whole argument. Every share is printed beside the count it came from and
//     above the total it is a share of, so a reader can see "1 of 40" and
//     discount it themselves. Withholding the percentage never withheld the
//     count, so it never protected anyone from over-reading a small number — it
//     just decided on their behalf which small numbers they were allowed to
//     compute. The next person who thinks a floor is obviously needed should have
//     to answer that.
//
//     ⚠️⚠️ THIS IS THE FOURTH POSITION THIS FILE HAS HELD IN THREE DAYS, and the
//     history is written down because a file that looks like it never had an
//     opinion invites the first position to be reinvented:
//       1. capped complementary suppression — REMOVED. "Sometimes declines and
//          publishes": the complexity of a guarantee with none of the protection,
//          and uncapped it hid 89 real leads to obscure an "unknown" of 2.
//       2. the "too few to report" fold — REMOVED (Scott, 2026-08-29: even one
//          record should show). Honest, but it concealed a count that needed no
//          concealing.
//       3. two reliability floors withholding SHARES only — REMOVED (Scott,
//          2026-08-31: "It does not matter if its only 1% or 0.1% it should still
//          be shown").
//       4. no floor. ← here.
//
//     ⚠️ `share()` still returns null, but ONLY for a zero denominator. That is
//     division by zero, not suppression, and it renders as a dash.
//
//     ⚠️ NONE OF THIS RELAXES RULE 6. Every position above was about a
//     SINGLE-DIMENSION count. A cross-tabulation is a different question and
//     still needs a real privacy floor before it ships.
//
//  5. ⚠️ SUM ONLY THE ADDITIVE METRICS: spend, impressions, leads. NEVER reach
//     or frequency — they count unique PEOPLE, so summing across days or across
//     ads double-counts anyone who saw the ad more than once. They are not in
//     `DemographicsStoredCell` at all, which is what makes the rule structural
//     rather than remembered.
//
//  6. ⚠️⚠️ THE ONE CONDITION THE WHOLE DECISION RESTS ON: these are
//     SINGLE-DIMENSION counts. "1 lead · female · 55-64 · Mount Druitt" is narrow in
//     a way "2 leads aged 65+" never is, so ANY CROSS-TABULATION OF TWO OR MORE
//     BREAKDOWN DIMENSIONS NEEDS A PRIVACY FLOOR DESIGNED PROPERLY BEFORE IT SHIPS
//     — real disclosure control, not a percentage cap. It will arrive looking like a
//     small feature request; it is not one.
//
//     ⚠️ PROMOTED TO THE HEADER, VERBATIM, 2026-08-31. It lived inside
//     `MIN_CELL_LEADS`'s doc comment, and that constant has since been deleted —
//     which would have deleted this with it. Moved in its own commit, BEFORE the
//     deletion, precisely because a comment cannot be tested and the ordering is
//     the only safeguard. It is now MORE load-bearing than it was, not less: this
//     file has NO reliability floor at all, so the boundary of that decision is
//     the only thing standing between us and a cross-tab panel that identifies
//     individuals.

import type { CurrencyAmount } from '../../types/reporting.js';


/** One stored demographics row, reduced to what the read path may use. */
export interface DemographicsStoredCell {
  /** YYYY-MM-DD. `windowStart === windowEnd` marks a DAILY row. */
  windowStart: string;
  windowEnd: string;
  dimension: string;
  value: string;
  /** total_leads — additive. */
  leads: number;
  /** impressions — additive. */
  impressions: number;
  /** spend in the row's native currency — additive WITHIN a currency only. */
  spend: number;
  currency: string;
}

export type DemographicsNoDataReason =
  | 'no-data' // nothing stored for this scope at all
  | 'no-covering-window' // rows exist, but none spans the whole selected range
  | 'invalid-range';

/**
 * Which stored grain answered the selected range, and over what period.
 *
 * ⚠️ `from`/`to` are the period the FIGURES describe — equal to the selected
 * range on the daily path, and the STORED window's own bounds on the fallback
 * path. Rule 2 lives in that distinction.
 */
export type DemographicsGrainChoice =
  /**
   * ⚠️ `from`/`to` are the FIRST and LAST day actually measured, which may be
   * NARROWER than the range asked for. `days` is how many distinct days carried
   * rows: `days` below the span from..to means there is a HOLE inside it, and
   * the period note says so. Carrying the count alongside the span is the same
   * device `CoverageTier` uses, for the same reason — a wide from/to on its own
   * hides a gap.
   */
  | { grain: 'daily'; from: string; to: string; days: number }
  | { grain: 'window'; from: string; to: string }
  | { grain: 'none'; reason: DemographicsNoDataReason };

const DAY_MS = 24 * 60 * 60 * 1000;

function isIsoDate(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s);
}

/** Every YYYY-MM-DD from `from` to `to` inclusive. Empty for an invalid range. */
export function eachDay(from: string, to: string): string[] {
  if (!isIsoDate(from) || !isIsoDate(to) || from > to) return [];
  const out: string[] = [];
  let t = Date.parse(from + 'T00:00:00Z');
  const end = Date.parse(to + 'T00:00:00Z');
  if (Number.isNaN(t) || Number.isNaN(end)) return [];
  // Guard against a pathological range producing an unbounded array.
  for (let i = 0; t <= end && i < 4000; i += 1, t += DAY_MS) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

function isDailyCell(cell: DemographicsStoredCell): boolean {
  return cell.windowStart === cell.windowEnd;
}

/**
 * Decide which grain — and which stored window — answers this range.
 *
 * ⚠️⚠️ ANY DAILY ROW IN RANGE WINS. A daily row is EXACT: it describes precisely
 * one day, so a run of them describes precisely those days, and the note names
 * them. A covering window is a compromise — it answers a WIDER question than the
 * one asked — so it is reached only when there is no daily data in the range at
 * all. See rule 1 in the header for the incident that corrected this, and for
 * why there is no coverage threshold.
 *
 * ⚠️ "LATEST" means the greatest `windowEnd`; ties break to the NARROWEST window
 * (greatest `windowStart`), because a tighter window sits closer to the range the
 * reader asked for and so overstates the period less.
 *
 * ⚠️⚠️ THAT PREFERENCE IS WHAT MAKES BACKFILL DEPTH A RECONCILIATION QUESTION,
 * AND IT IS WHY `demographics-full` REACHES 13 MONTHS RATHER THAN 25. Because
 * ANY daily row in range wins, every period a daily backfill reaches DISPLACES
 * the wide-window row that used to answer it — the figures do not merge, one
 * population simply stops being consulted — so each displaced period is a figure
 * that can move, on a panel a client may already have read.
 *
 * At 13 months the two populations LAYER BY TIME rather than overlapping:
 * periods older than the boundary have no daily rows at all, so they go on
 * reading their wide windows untouched, and only ONE boundary has to reconcile
 * instead of two years of it across the fleet. That is the strongest argument
 * for the depth — stronger than its cost — and it is why deepening it later is
 * not a free change: it would re-open every period it newly reaches.
 *
 * ⚠️ THE LEGACY WIDE ROWS ARE THEREFORE LOAD-BEARING, not dead weight awaiting
 * cleanup. Past the boundary they are the ONLY thing that answers a range at
 * all, so deleting them would turn older history into "no data" rather than
 * tidying anything up.
 */
export function selectDemographicsWindow(
  cells: DemographicsStoredCell[],
  range: { from: string; to: string },
): DemographicsGrainChoice {
  const days = eachDay(range.from, range.to);
  if (days.length === 0) return { grain: 'none', reason: 'invalid-range' };
  if (cells.length === 0) return { grain: 'none', reason: 'no-data' };

  const dailyDays = new Set<string>();
  for (const cell of cells) {
    if (isDailyCell(cell) && cell.windowStart >= range.from && cell.windowStart <= range.to) {
      dailyDays.add(cell.windowStart);
    }
  }
  if (dailyDays.size > 0) {
    // ⚠️ The span is the MEASURED days' own first and last, never the selected
    // range's — rule 2 applied to the daily path too. Echoing the range back
    // would be exactly the lie the fallback path is careful not to tell.
    const measured = [...dailyDays].sort();
    return {
      grain: 'daily',
      from: measured[0] as string,
      to: measured[measured.length - 1] as string,
      days: dailyDays.size,
    };
  }

  // The fallback: windows that span the WHOLE range. An overlap is not enough —
  // a window covering half the range describes a different period than the one
  // asked for, and no label could honestly describe the result.
  let best: { from: string; to: string } | null = null;
  for (const cell of cells) {
    if (cell.windowStart > range.from || cell.windowEnd < range.to) continue;
    if (
      best === null ||
      cell.windowEnd > best.to ||
      (cell.windowEnd === best.to && cell.windowStart > best.from)
    ) {
      best = { from: cell.windowStart, to: cell.windowEnd };
    }
  }
  if (!best) return { grain: 'none', reason: 'no-covering-window' };
  // ⚠️ Reached ONLY with zero daily rows in range — which is why there is no
  // longer a `dailyCoverage: 'partial'` case to report: partial daily coverage
  // now takes the daily path. The field was REMOVED rather than left dead. A
  // branch that can no longer fire is a rule that reads as live and is not.
  return { grain: 'window', from: best.from, to: best.to };
}

/** The cells the chosen grain actually reads. */
export function cellsForChoice<T extends DemographicsStoredCell>(
  cells: T[],
  choice: DemographicsGrainChoice,
): T[] {
  if (choice.grain === 'none') return [];
  if (choice.grain === 'daily') {
    return cells.filter(
      (c) => isDailyCell(c) && c.windowStart >= choice.from && c.windowStart <= choice.to,
    );
  }
  return cells.filter((c) => c.windowStart === choice.from && c.windowEnd === choice.to);
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

export interface DemographicsRow {
  value: string;
  leads: number;
  impressions: number;
  /** Per-currency, NEVER blended — the money rule the whole warehouse follows. */
  spend: CurrencyAmount[];
  /** Share of this breakdown's own lead total. null = suppressed / unmeasured. */
  leadShare: number | null;
  /** Share of this breakdown's own impression total. null = unmeasured. */
  impressionShare: number | null;
}

export interface DemographicsDimension {
  dimension: string;
  /**
   * EVERY cell, ordered for display (see `sortRows`). Nothing is folded and
   * nothing is dropped, so these sum to `totals` exactly.
   */
  rows: DemographicsRow[];
  /** ⚠️ The denominator every share above is a share OF. Rule 3. */
  totals: { leads: number; impressions: number; spend: CurrencyAmount[] };

}

interface Acc {
  leads: number;
  impressions: number;
  spend: Map<string, number>;
}

function emptyAcc(): Acc {
  return { leads: 0, impressions: 0, spend: new Map() };
}

function addTo(acc: Acc, cell: DemographicsStoredCell): void {
  // ⚠️ Additive metrics ONLY. Reach and frequency are absent from the cell type
  // by design (rule 5) — there is nothing here to accidentally sum.
  acc.leads += cell.leads;
  acc.impressions += cell.impressions;
  acc.spend.set(cell.currency, (acc.spend.get(cell.currency) ?? 0) + cell.spend);
}

function toCurrencyAmounts(spend: Map<string, number>): CurrencyAmount[] {
  return [...spend.entries()]
    .map(([currency, amount]) => ({ currency, amount }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

/**
 * ⚠️⚠️ NULL ONLY FOR A ZERO DENOMINATOR — that is division by zero, not
 * suppression, and a template renders it as a dash. There is no floor: see rule
 * 4 for the four positions this file has held and why this is the last one.
 *
 * ⚠️ The distinction that survives all of it: 0% means "measured, and it was
 * none", so a real zero must never render as a dash and a null must never render
 * as 0%.
 */
function share(numerator: number, denominator: number): number | null {
  if (denominator <= 0) return null;
  return numerator / denominator;
}

/** The leading number of an age bracket ('25-34' -> 25, '65+' -> 65). */
function ageOrder(value: string): number {
  const m = /^(\d+)/.exec(value.trim());
  return m ? Number(m[1]) : Number.POSITIVE_INFINITY;
}

/**
 * Display order. Age reads as a SEQUENCE, so it sorts by bracket rather than by
 * response — an out-of-order age table is harder to read than an unranked one.
 * Every other dimension answers "who responded most", so it sorts by leads.
 */
export function sortRows(dimension: string, rows: DemographicsRow[]): DemographicsRow[] {
  const copy = [...rows];
  if (dimension === 'age') {
    copy.sort((a, b) => ageOrder(a.value) - ageOrder(b.value) || a.value.localeCompare(b.value));
    return copy;
  }
  copy.sort(
    (a, b) => b.leads - a.leads || b.impressions - a.impressions || a.value.localeCompare(b.value),
  );
  return copy;
}

/**
 * Aggregate one dimension's cells into a display-ready breakdown.
 *
 * ⚠️ Every cell is named. The floors withhold SHARES, never rows, so the rows
 * sum to `totals` exactly and a reader can check the table against its own
 * denominator.
 */
export function aggregateDimension(
  dimension: string,
  cells: DemographicsStoredCell[],
): DemographicsDimension {
  const byValue = new Map<string, Acc>();
  const total = emptyAcc();
  for (const cell of cells) {
    if (cell.dimension !== dimension) continue;
    let acc = byValue.get(cell.value);
    if (!acc) {
      acc = emptyAcc();
      byValue.set(cell.value, acc);
    }
    addTo(acc, cell);
    addTo(total, cell);
  }

  const named: DemographicsRow[] = [];

  for (const [value, acc] of byValue) {
    // ⚠️ EVERY cell gets its share, at every size — rule 4. The only null is a
    // zero denominator, which `share` handles, and a MEASURED ZERO keeps its 0%
    // because that is real information.
    named.push({
      value,
      leads: acc.leads,
      impressions: acc.impressions,
      spend: toCurrencyAmounts(acc.spend),
      leadShare: share(acc.leads, total.leads),
      impressionShare: share(acc.impressions, total.impressions),
    });
  }

  return {
    dimension,
    rows: sortRows(dimension, named),
    totals: {
      leads: total.leads,
      impressions: total.impressions,
      spend: toCurrencyAmounts(total.spend),
    },
  };
}

/** Aggregate every dimension present in `cells`, in the given display order. */
export function aggregateDemographics(
  cells: DemographicsStoredCell[],
  order: readonly string[],
): DemographicsDimension[] {
  const present = new Set(cells.map((c) => c.dimension));
  const ordered = order.filter((d) => present.has(d));
  for (const d of [...present].sort()) if (!ordered.includes(d)) ordered.push(d);
  return ordered.map((d) => aggregateDimension(d, cells));
}

// ---------------------------------------------------------------------------
// Wording
//
// ⚠️ The sentences live HERE, not in the component. Two reasons, and the second
// is practical: the wording of an honesty caveat is the part worth pinning
// (the `pullFreshnessMessage` / `conversionNote` precedent), and this suite sits
// close enough to its per-test timeout that one extra jsdom file tips several
// unrelated heavy render tests over.
//
// Dates are formatted by the CALLER and passed as a formatter, so there is no
// second argument that has to stay in step with the first.
// ---------------------------------------------------------------------------

export type FormatDate = (isoDate: string) => string;

/**
 * ⚠️⚠️ RULE 2 LIVES HERE. On the fallback path this states the STORED window and
 * says outright that it is not the range the reader selected. Widening it to
 * match the report is the one edit that turns a correct panel into a misleading
 * one, so the selected range is named in the same breath as the stored one.
 */
/**
 * ⚠️⚠️ A STORED WINDOW CAN END IN THE FUTURE, AND THE PANEL MUST NOT SAY SO.
 *
 * `computePullWindow` pads the top end by a day, which is what makes the current
 * day's data reachable across the UTC/Brisbane offset — correct, and not to be
 * removed. But it means `window_end` is routinely TOMORROW, and a period that
 * ends in the future is a claim about data that cannot exist.
 *
 * ⚠️ DISPLAY-SIDE ONLY. `window_end` is part of the demographics ROW KEY
 * (rowKeySql.ts), so rewriting it at write time would fragment the MERGE dedup
 * and orphan every stored row. Clamp what we SAY, never what we store.
 *
 * `today` is the VIEWER'S local calendar date — see `toLocalYmd`; taking it in
 * UTC would name yesterday for a Brisbane reader all morning, clamping the window
 * a day short every day before 10:00.
 */
export function clampWindowEndToToday(windowEnd: string, today: string): string {
  return windowEnd > today ? today : windowEnd;
}

export function demographicsPeriodNote(
  choice: DemographicsGrainChoice,
  range: { from: string; to: string },
  fmt: FormatDate,
  today: string,
): string | null {
  if (choice.grain === 'none') return null;
  if (choice.grain === 'daily') {
    // ⚠️ NOT clamped: on this path the dates are DAYS WE HOLD ROWS FOR, so they
    // are in the past by construction — there is nothing to clamp, and clamping
    // would make the sentence false.
    const rangeDays = eachDay(range.from, range.to).length;
    if (choice.days >= rangeDays) {
      return `covers ${fmt(choice.from)} – ${fmt(choice.to)}, the period you selected.`;
    }
    // ⚠️⚠️ PARTIAL DAILY COVERAGE, STATED. These figures are exact for the days
    // they cover and those days are NARROWER than the range asked for, so the
    // note names both — the same shape as the fallback sentence, for the same
    // reason: every other panel on the page is describing the selected range.
    const span = eachDay(choice.from, choice.to).length;
    const hole =
      choice.days < span
        ? ` ${choice.days} of the ${span} days in that span carried audience data; the rest were not measured.`
        : '';
    return (
      `covers ${fmt(choice.from)} – ${fmt(choice.to)}, NOT the whole ` +
      `${fmt(range.from)} – ${fmt(range.to)} you selected. day-by-day audience data has not ` +
      'landed for every day of your range, so these figures describe the days it has.' +
      hole
    );
  }
  const end = clampWindowEndToToday(choice.to, today);
  return (
    `covers ${fmt(choice.from)} – ${fmt(end)}, NOT the ${fmt(range.from)} – ${fmt(range.to)} ` +
    'you selected. meta returns audience breakdowns per pull window rather than per day, so this is ' +
    'the narrowest stored window that covers your range.'
  );
}

/** Why the panel is showing nothing. ⚠️ Never silence, and never a nearer window. */
export function demographicsEmptyMessage(
  reason: DemographicsNoDataReason,
  range: { from: string; to: string },
  fmt: FormatDate,
): string {
  if (reason === 'invalid-range') return 'pick a date range to see the audience breakdown.';
  if (reason === 'no-data') {
    return 'no audience data has been pulled for this account yet.';
  }
  return (
    `no stored audience window covers ${fmt(range.from)} – ${fmt(range.to)}. ` +
    'meta returns these breakdowns per pull window, and showing the nearest window instead would ' +
    'describe a different period than the one you asked for — so nothing is shown. try a wider range.'
  );
}

/**
 * ⚠️ RULE 3. The denominator, stated.
 *
 * ⚠️⚠️ IT SHORTENED ON 2026-09-01 AND MUST NEVER DISAPPEAR. Every other caveat on
 * this report lost its second half in that pass; this one kept its bridge —
 * "not the headline total" — because rule 4's argument depends on it. Both
 * reliability floors were deleted the day before, on Scott's instruction, which
 * leaves THIS SENTENCE as the only thing making an unfloored share honest. The
 * audience leads will not equal the report's headline leads, and a client who
 * spots that gap unaided stops trusting the whole page.
 *
 * ⚠️ What went is the EXPLANATION of why they differ — that Meta withholds
 * unknown buckets and dedups across breakdowns. That is our problem, not the
 * client's, and it is the half that made this a two-read sentence.
 *
 * ⚠️ It still fires UNCONDITIONALLY, at every denominator including 1 and 0.
 * Make it conditional and rule 4 stops being true.
 */
export function demographicsDenominatorNote(dimension: DemographicsDimension): string {
  const n = dimension.totals.leads;
  return `shares are of the ${n} lead${n === 1 ? '' : 's'} in this breakdown, not the headline total.`;
}

// ⚠️⚠️ `demographicsFloorNote` WAS DELETED HERE (2026-08-31), and nothing replaces
// it. It explained the two reliability floors; there are no floors, so there is
// nothing to explain. Its absence is not an oversight.
//
// ⚠️ `demographicsDenominatorNote` above is now the ONLY honesty device on the
// panel, which is exactly why it fires UNCONDITIONALLY. Rule 4's argument depends
// on it: every share is shown, at every size, and what makes that honest is that
// the count and the denominator are printed with it. Make that note conditional
// and rule 4 stops being true.
